import { useTranslation } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Gem, Share2, Zap, MapPin, Star, Headphones } from 'lucide-react';

export default function Landing() {
  const { t, language } = useTranslation();

  const quickFeatures = [
    { icon: Zap, title: language === 'fr' ? 'Décide en temps réel' : 'Decide in real time' },
    { icon: Clock, title: language === 'fr' ? 'Gagne du temps' : 'Save time' },
    { icon: Gem, title: language === 'fr' ? 'Découvre des pépites' : 'Discover gems' },
    { icon: Share2, title: language === 'fr' ? 'Partage simplement' : 'Share easily' },
  ];

  const features = [
    { icon: Zap, title: 'Instant Pulse', desc: language === 'fr' ? "Instant Pulse vous donne des idées immédiates selon votre temps, votre humeur et votre localisation." : 'Instant Pulse gives you instant ideas based on your time, mood, and location.' },
    { icon: MapPin, title: language === 'fr' ? "C'est quoi ici ?" : "What's here?", desc: language === 'fr' ? 'Identifiez n\'importe quel lieu avec une photo' : 'Identify any place with a photo' },
    { icon: Headphones, title: 'Audio Guides', desc: language === 'fr' ? 'Guides immersifs générés par IA' : 'AI-generated immersive guides' },
    { icon: Share2, title: language === 'fr' ? 'Partage' : 'Share', desc: language === 'fr' ? 'Partagez vos découvertes facilement' : 'Share your discoveries easily' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Dark */}
      <section className="relative pt-24 sm:pt-28 pb-16 sm:pb-20 px-4 sm:px-6 bg-gradient-to-br from-[#1a0a2e] via-[#2d1b4e] to-[#1a0a2e]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Text Content */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-900/50 border border-purple-700/50 text-fuchsia-300 text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                <span>Smart Mobility Companion</span>
              </div>
              
              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
                {language === 'fr' ? (
                  <>Quelle sera ma prochaine<br /><span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-fuchsia-400 bg-clip-text text-transparent">expérience ?</span></>
                ) : (
                  <>What should I<br /><span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-fuchsia-400 bg-clip-text text-transparent">do next?</span></>
                )}
              </h1>
              
              {/* Subline */}
              <p className="text-base sm:text-lg text-purple-200 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {language === 'fr' 
                  ? "LifePulse vous aide à découvrir et vivre de vrais moments — maintenant, plus tard ou lors de votre prochain voyage."
                  : "LifePulse helps you discover and live real moments — now, later, or on your next trip."}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
                <Link to="/login">
                  <Button className="w-full sm:w-auto rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600 text-white px-8 py-6 text-base font-semibold shadow-xl shadow-fuchsia-500/30" data-testid="hero-cta-btn">
                    {language === 'fr' ? 'Découvrir LifePulse' : 'Discover LifePulse'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button variant="outline" className="w-full sm:w-auto rounded-full border-2 border-purple-600 bg-purple-900/30 text-purple-200 hover:bg-purple-800/50 hover:text-white px-8 py-6 text-base font-medium">
                    {language === 'fr' ? 'Bientôt disponible' : 'Coming Soon'}
                  </Button>
                </a>
              </div>

              {/* Quick Features */}
              <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto lg:mx-0">
                {quickFeatures.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-purple-900/40 border border-purple-700/30 backdrop-blur-sm">
                    <feature.icon className="w-5 h-5 text-fuchsia-400 flex-shrink-0" />
                    <span className="text-sm text-purple-100 font-medium whitespace-nowrap">{feature.title}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            {/* Right: Times Square Image with Card */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/20 to-purple-500/20 rounded-3xl blur-3xl" />
                <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-purple-900/50 border border-purple-700/30">
                  <img src="https://images.pexels.com/photos/1089194/pexels-photo-1089194.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop" alt="Times Square" className="w-full h-[500px] object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a2e]/80 via-transparent to-transparent" />
                  
                  {/* Featured Card Overlay */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white rounded-2xl p-5 shadow-xl">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                            {language === 'fr' ? 'Ouvert' : 'Open'}
                          </span>
                          <h3 className="font-bold text-gray-900 text-lg mt-1">Rooftop Bar</h3>
                          <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                            <MapPin className="w-4 h-4 text-fuchsia-500" />
                            <span>Times Square, NYC</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-lg">
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          <span className="text-sm font-semibold text-amber-700">4.8</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        {language === 'fr' ? 'Vue panoramique sur Manhattan avec cocktails signature' : 'Panoramic view of Manhattan with signature cocktails'}
                      </p>
                      <div className="flex gap-2">
                        <Button className="flex-1 bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white rounded-xl text-sm py-2.5">
                          {language === 'fr' ? 'Y aller' : 'Go there'}
                        </Button>
                        <Button variant="outline" className="w-12 h-10 p-0 rounded-xl border-purple-200 hover:bg-purple-50">
                          <Headphones className="w-5 h-5 text-purple-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mobile Image */}
            <div className="lg:hidden relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-purple-900/50 border border-purple-700/30">
                <img src="https://images.pexels.com/photos/1089194/pexels-photo-1089194.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" alt="Times Square" className="w-full h-[280px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a2e]/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white rounded-xl p-4 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{language === 'fr' ? 'Ouvert' : 'Open'}</span>
                        <h3 className="font-bold text-gray-900 mt-1">Rooftop Bar</h3>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <span className="text-sm font-semibold">4.8</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - WHITE BACKGROUND */}
      <section id="features" className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Tout ce dont vous avez besoin' : 'Everything you need'}
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto text-sm sm:text-base">
              {language === 'fr' 
                ? 'De la découverte spontanée à la planification complète de voyage'
                : 'From spontaneous discovery to complete trip planning'}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group p-5 sm:p-6 rounded-2xl bg-gray-50 border border-gray-100 hover:border-fuchsia-200 hover:bg-fuchsia-50/50 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-fuchsia-500/30">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Instant Pulse Section - DARK BACKGROUND */}
      <section id="instant-pulse" className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-[#1a0a2e] via-[#2d1b4e] to-[#1a0a2e]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-fuchsia-500/20 border border-fuchsia-500/30 text-fuchsia-300 text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                <span>Instant Pulse</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                {language === 'fr' ? 'Des idées instantanées, adaptées à vous' : 'Instant ideas, tailored to you'}
              </h2>
              <p className="text-purple-200 max-w-2xl mx-auto text-sm sm:text-base">
                {language === 'fr' 
                  ? "Instant Pulse analyse votre contexte et vous propose les meilleures expériences en quelques secondes."
                  : "Instant Pulse analyzes your context and suggests the best experiences in seconds."}
              </p>
            </motion.div>
          </div>
          
          {/* Instant Pulse Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { 
                icon: Clock, 
                title: language === 'fr' ? 'Temps disponible' : 'Time available',
                desc: language === 'fr' ? '30 min, 2h ou une journée' : '30 min, 2h or a full day'
              },
              { 
                icon: Gem, 
                title: language === 'fr' ? 'Votre humeur' : 'Your mood',
                desc: language === 'fr' ? 'Détente, aventure, culture...' : 'Relaxation, adventure, culture...'
              },
              { 
                icon: MapPin, 
                title: language === 'fr' ? 'Votre position' : 'Your location',
                desc: language === 'fr' ? 'Suggestions autour de vous' : 'Suggestions around you'
              },
              { 
                icon: Share2, 
                title: language === 'fr' ? 'Avec qui ?' : 'With whom?',
                desc: language === 'fr' ? 'Solo, famille, amis, collègues' : 'Solo, family, friends, colleagues'
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-5 rounded-2xl bg-purple-900/40 border border-purple-700/30 text-center hover:bg-purple-900/60 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-fuchsia-500/30">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-purple-300">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - WHITE BACKGROUND */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-fuchsia-600 to-pink-600 rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              {language === 'fr' ? 'LifePulse transforme la découverte en vrais moments.' : 'LifePulse turns discovery into real moments.'}
            </h2>
            <p className="text-white/80 mb-8 max-w-lg mx-auto text-sm sm:text-base">
              {language === 'fr' 
                ? "Rejoignez des milliers d'explorateurs qui transforment chaque moment en aventure."
                : "Join thousands of explorers who turn every moment into an adventure."}
            </p>
            <Link to="/login">
              <Button className="rounded-full bg-white text-fuchsia-600 hover:bg-white/90 px-8 py-6 text-base font-semibold shadow-xl">
                {language === 'fr' ? 'Découvrir LifePulse' : 'Discover LifePulse'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
